import React from "react";
import { i18n } from "../../../translate/i18n";
import AppTableActionMenu from "../../../components/AppTableActionMenu";

function IconLink({ className = "size-3 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
			<path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
		</svg>
	);
}

function IconSettings({ className = "size-3 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="12" cy="12" r="3" />
			<path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.01a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
		</svg>
	);
}

function IconMore() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="12" cy="12" r="1" />
			<circle cx="19" cy="12" r="1" />
			<circle cx="5" cy="12" r="1" />
		</svg>
	);
}

function IconPlug({ className = "size-5 text-primary" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M12 22v-5" />
			<path d="M9 8V2" />
			<path d="M15 8V2" />
			<path d="M18 8v2a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z" />
		</svg>
	);
}

const STATUS_BADGE = {
	connected: "bg-success/10 text-success",
	disconnected: "bg-default-200 text-default-600 dark:bg-default-700 dark:text-default-300",
	coming_soon: "bg-warning/10 text-warning",
};

function statusLabel(status) {
	if (status === "connected") return i18n.t("integrations.status.connected");
	if (status === "coming_soon") return i18n.t("integrations.status.comingSoon");
	return i18n.t("integrations.status.disconnected");
}

function IntegrationLogoFrame({ integration }) {
	const { Logo, logoVariant = "icon" } = integration;
	const isWordmark = logoVariant === "wordmark";

	return (
		<div
			className={`mx-auto flex size-16 shrink-0 items-center justify-center rounded-full outline outline-4 outline-offset-4 ${isWordmark
				? "bg-[#0030B9] outline-[#0030B9]/20"
				: "bg-default-100 outline-default-100 dark:bg-default-200 dark:outline-default-800"
				}`}
		>
			<Logo />
		</div>
	);
}

export default function IntegrationCard({ integration, onConfigure }) {
	const { nameKey, descriptionKey, categoryKey, status, docsUrl } = integration;

	const name = i18n.t(nameKey);

	const menuItems = [
		{
			key: "configure",
			label: i18n.t("integrations.actions.configure"),
			icon: <IconSettings />,
			onClick: () => onConfigure(integration),
		},
		docsUrl && {
			key: "docs",
			label: i18n.t("integrations.actions.documentation"),
			icon: <IconLink />,
			onClick: () => window.open(docsUrl, "_blank", "noopener,noreferrer"),
		},
	].filter(Boolean);

	return (
		<div className="card">
			<div className="card-body">
				<div className="mb-4 flex items-center justify-between gap-5">
					<IconPlug className="size-5 fill-primary/10 text-primary" />

					<AppTableActionMenu
						triggerIcon={<IconMore />}
						triggerAriaLabel={i18n.t("integrations.actions.menuAria", { name })}
						items={menuItems}
					/>
				</div>

				<div>
					<IntegrationLogoFrame integration={integration} />

					<div className="mt-4 mb-6 text-center">
						<h6 className="text-base font-semibold text-default-800 dark:text-default-100">
							{name}
						</h6>
						<p className="text-sm text-default-500 dark:text-default-400">
							{i18n.t(descriptionKey)}
						</p>
					</div>

					<div className="grid grid-cols-3">
						<div className="p-2 text-center text-sm">
							<h6 className="mb-1 font-semibold text-default-800 dark:text-default-100">
								<span
									className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_BADGE[status] || STATUS_BADGE.disconnected}`}
								>
									{statusLabel(status)}
								</span>
							</h6>
							<p className="text-default-500 dark:text-default-400">
								{i18n.t("integrations.metrics.status")}
							</p>
						</div>

						<div className="border-e border-s border-dashed border-default-200 p-2 text-center text-sm dark:border-white/14">
							<h6 className="mb-1 font-semibold text-default-800 dark:text-default-100">
								{i18n.t(categoryKey)}
							</h6>
							<p className="text-default-500 dark:text-default-400">
								{i18n.t("integrations.metrics.category")}
							</p>
						</div>

						<div className="p-2 text-center text-sm">
							<h6 className="mb-1 font-semibold text-default-800 dark:text-default-100">
								{integration.config?.updatedAt
									? new Date(integration.config.updatedAt).toLocaleString("pt-BR")
									: "-"}
							</h6>
							<p className="text-default-500 dark:text-default-400">
								{i18n.t("integrations.metrics.lastSync")}
							</p>
						</div>
					</div>
					{status !== "connected" || integration.id === "google-calendar" ? (
						<button
							type="button"
							className="btn btn-sm mt-5 w-full bg-primary text-white"
							onClick={() => onConfigure(integration)}
						>
							{i18n.t("integrations.actions.configure")}
						</button>
					) : null}
				</div>
			</div>
		</div>
	);
}
